import React, { useEffect, useState } from "react";
import { Modal, Skeleton } from "antd";
import dayjs from "dayjs";
import CgIcon from "../common/CgIcon";
import {
  ListTable,
  RefCell,
  StackedCell,
  CellRow,
  DimCell,
  NumCell,
  PHARMACY_PAGE_SIZE,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { getPharmacyMedicineOrdersApi } from "../../../services/pharmacyMedicines.services";
import { formatAmount } from "../common/money";
import { getErrorMessage } from "../../../helpers/error";
import styles from "./medicineOrders.module.css";

/*
 * Every order one medicine has been put on.
 *
 * Opened from the medicine's own page, so the medicine is never in question -
 * the dialog is handed the record the page is already showing and asks the
 * server about that.
 */

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/*
 * One row of the endpoint's own shape.
 *
 * A row is one medicine on one order, so the figures on it are that line's -
 * the quantity dispensed against it, the rate it went out at, the batches it
 * came from. The order around it supplies the number, the date and the
 * patient.
 */
const readRow = (row, index) => {
  const batches = Array.isArray(row?.batches) ? row.batches : [];

  /* What went out, or what was asked for where nothing has yet. A line still
     pending reads as the quantity it is waiting on, not as nought. */
  const dispensed = num(row?.dispense_qty) || 0;
  const requested = num(row?.requested_qty) || 0;
  const qty = dispensed || requested;

  /* The rate the line was charged at. `override_rate` is what the pharmacist
     set; failing that, the price of the batch it came off. */
  const rate = num(row?.override_rate) ?? num(batches[0]?.selling_price) ?? null;
  const discount = num(row?.discount) || 0;

  const patient = row?.patient || null;

  return {
    key: row?.id ?? `row-${index}`,
    orderNumber: row?.order_number || null,
    date: row?.order_date ? dayjs(row.order_date) : null,
    patient: patient?.name || patient?.counter_sale_customer || null,
    saleType: row?.sale_type || null,
    qty,
    dispensed,
    requested,
    rate,
    /* Nothing on the line totals it, so it is worked out: what went out at
       the rate it went out at, less what came off it. */
    amount: rate === null ? null : Math.max(qty * rate - discount, 0),
    /* A line can be filled from more than one batch, and each has its own
       number and its own expiry. They are kept as pairs and sorted nearest
       first, so the two columns always name the same batch as each other. */
    batches: batches
      .map((batch) => ({
        number: batch?.batch_number || null,
        expiry: batch?.expiry || null,
      }))
      .filter((batch) => batch.number || batch.expiry)
      .sort((a, b) => dayjs(a.expiry).valueOf() - dayjs(b.expiry).valueOf()),
  };
};

/*
 * One batch's worth of a column, and a count of the others.
 *
 * Both batch columns read the same list the same way, so the leading value
 * and the "+2" after it always refer to the same batch in each - and the
 * whole list is in the tooltip for anyone who needs it.
 */
const BatchSpan = ({ rows, read, title }) => {
  const values = (rows || []).map(read).filter(Boolean);
  if (!values.length) return <DimCell>-</DimCell>;
  const [first, ...rest] = values;
  return (
    <DimCell>
      <span title={(rows || []).map(title).filter(Boolean).join(" · ")}>
        {first}
        {rest.length ? ` +${rest.length}` : ""}
      </span>
    </DimCell>
  );
};

const MedicineOrdersModal = ({ open, onClose, medicine }) => {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState("");

  /* Either shape of record: the page's own medicine, or a picked option. */
  const subjectId = medicine?.id ?? medicine?.value ?? null;
  const subjectName = medicine?.name ?? medicine?.label ?? "";

  /* Opened afresh each time - a page read last time is not this one's first. */
  useEffect(() => {
    if (open) return;
    setRows([]);
    setCount(0);
    setPage(1);
    setFailed("");
  }, [open]);

  useEffect(() => {
    setPage(1);
  }, [subjectId]);

  useEffect(() => {
    if (!open || !subjectId) {
      setRows([]);
      setCount(0);
      return undefined;
    }
    let live = true;
    setLoading(true);
    setFailed("");

    getPharmacyMedicineOrdersApi(
      subjectId,
      new URLSearchParams({ page, limit: PHARMACY_PAGE_SIZE })
    )
      .then((res) => {
        if (!live) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : data?.results || [];
        setRows(list.map(readRow));
        setCount(Number(data?.count) || list.length);
      })
      .catch((err) => {
        if (!live) return;
        setRows([]);
        setCount(0);
        setFailed(getErrorMessage(err));
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [open, subjectId, page]);

  const columns = [
    {
      title: "Order No",
      key: "order_number",
      render: (_, row) => <RefCell>{row.orderNumber}</RefCell>,
    },
    {
      title: "Date",
      key: "date",
      render: (_, row) => (
        <DimCell>{row.date ? row.date.format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      /*
       * Who it went to, with how it bills beside the name - in one column
       * rather than two.
       *
       * The chip wears the organisation's colour, not the per-type colours
       * the Orders list gives it. There, the type is one signal among several
       * and the colour helps pick it out; here it is the only chip in the
       * column and every row carries one, so four different colours running
       * down the list read as four kinds of alarm rather than as one fact
       * stated twenty times.
       *
       * The name and the type, and nothing else: the MRN and the bed are
       * facts about the patient, not about this medicine going out, and a
       * dialog about one medicine does not need the ward round with it.
       */
      title: "Patient",
      key: "patient",
      render: (_, row) => (
        <CellRow>
          <StackedCell name={row.patient || "-"} />
          {row.saleType ? (
            <span className={styles.typeTag}>{row.saleType}</span>
          ) : null}
        </CellRow>
      ),
    },
    {
      title: "Qty",
      key: "qty",
      align: "right",
      render: (_, row) => <NumCell>{row.qty || "-"}</NumCell>,
    },
    {
      title: "Rate",
      key: "rate",
      align: "right",
      render: (_, row) => (
        <NumCell>{row.rate === null ? "-" : formatAmount(row.rate)}</NumCell>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, row) => (
        <NumCell>{row.amount === null ? "-" : formatAmount(row.amount)}</NumCell>
      ),
    },
    {
      /* The batch it came off. A line filled from two leads with the one
         expiring soonest and counts the rest. */
      title: "Batch No",
      key: "batch_number",
      render: (_, row) => (
        <BatchSpan
          rows={row.batches}
          read={(batch) => batch.number}
          title={(batch) => batch.number}
        />
      ),
    },
    {
      title: "Expiry",
      key: "expiry",
      render: (_, row) => (
        <BatchSpan
          rows={row.batches}
          read={(batch) => (batch.expiry ? dayjs(batch.expiry).format("DD MMM YYYY") : null)}
          title={(batch) =>
            batch.expiry ? dayjs(batch.expiry).format("DD MMM YYYY") : null
          }
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className={styles.head}>
          <span className={styles.headTitle}>Orders History</span>
          {subjectName ? (
            <span className={styles.headSub}>{subjectName}</span>
          ) : null}
        </div>
      }
      open={open}
      onCancel={onClose}
      centered
      width={1040}
      destroyOnClose
      footer={null}
    >
      {loading && !rows.length ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : failed ? (
        <div className={styles.empty}>
          <CgIcon name="alert" size={22} stroke={1.9} />
          <span>{failed}</span>
        </div>
      ) : !rows.length ? (
        <div className={styles.empty}>
          <CgIcon name="ledger" size={22} stroke={1.8} />
          <span>
            {subjectName || "This medicine"} has not been put on an order yet.
          </span>
        </div>
      ) : (
        <>
          <div className={styles.count}>
            {count} {count === 1 ? "order" : "orders"}
          </div>
          <ListTable
            loading={loading}
            dataSource={rows}
            columns={columns}
            rowKey="key"
            pagination={getTablePagination({
              current: page,
              pageSize: PHARMACY_PAGE_SIZE,
              total: count,
              onChange: (next) => setPage(next),
            })}
          />
        </>
      )}
    </Modal>
  );
};

export default MedicineOrdersModal;
